import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface TopCreator {
  id: string;
  avatar_url: string | null;
  full_name: string | null;
  event_count: number;
}

export default function HeroSection() {
  const [creators, setCreators] = useState<TopCreator[]>([]);
  const [creatorCount, setCreatorCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchTopCreators = async () => {
      try {
        const [eventsRes, storesRes] = await Promise.all([
          supabase.from('events').select('organizer_id').eq('is_published', true),
          supabase.from('store_profiles').select('user_id'),
        ]);

        const eventCreatorIds = new Set<string>(
          (eventsRes.data || []).map((e: { organizer_id: string }) => e.organizer_id)
        );
        const storeOwnerIds = new Set<string>(
          (storesRes.data || []).map((s: { user_id: string }) => s.user_id)
        );

        const allUniqueIds = new Set([...eventCreatorIds, ...storeOwnerIds]);
        setCreatorCount(allUniqueIds.size);

        // Build event count map for sorting
        const countMap: Record<string, number> = {};
        (eventsRes.data || []).forEach((e: { organizer_id: string }) => {
          countMap[e.organizer_id] = (countMap[e.organizer_id] || 0) + 1;
        });

        // All unique IDs from both event creators and store owners
        const allIds = Array.from(allUniqueIds);

        if (allIds.length === 0) return;

        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url')
          .in('id', allIds);

        if (profilesError || !profiles) return;

        // Sort: event creators with most events first, then store-only owners
        const sorted = profiles
          .map((p) => ({
            id: p.id,
            full_name: p.full_name,
            avatar_url: p.avatar_url,
            event_count: countMap[p.id] || 0,
          }))
          .sort((a, b) => b.event_count - a.event_count)
          .slice(0, 10);

        setCreators(sorted);
      } catch (err) {
        console.error('Error fetching top creators:', err);
      }
    };

    fetchTopCreators();
  }, []);

  const getInitial = (name: string | null) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  const gradients = [
    'from-teal-400 to-emerald-400',
    'from-rose-400 to-orange-300',
    'from-amber-400 to-yellow-300',
    'from-cyan-400 to-teal-300',
    'from-pink-400 to-rose-300',
    'from-violet-400 to-purple-300',
    'from-orange-400 to-amber-300',
    'from-green-400 to-teal-300',
    'from-red-400 to-pink-300',
    'from-indigo-400 to-cyan-300',
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-700 to-blue-800">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
      </div>

      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-20 sm:pt-0">
        <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 sm:mb-8 leading-tight">
          Discover Events That
          <br />
          <span className="bg-gradient-to-r from-yellow-400 to-pink-500 bg-clip-text text-transparent">
            Match Your Vibe
          </span>
        </h1>

        <p className="text-base sm:text-xl md:text-2xl text-white/80 mb-8 sm:mb-12 font-light px-4">
          Connect, Follow, Attend - Where Events Meet Social Growth
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16 px-4">
          <Link
            to="/events"
            className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm sm:text-base font-semibold rounded-full hover:shadow-2xl transition-all flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap"
          >
            <i className="ri-compass-3-line text-lg sm:text-xl"></i>
            Explore Free Events
            <i className="ri-arrow-right-line text-lg sm:text-xl group-hover:translate-x-1 transition-transform"></i>
          </Link>
          <Link
            to="/create"
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white text-white text-sm sm:text-base font-semibold rounded-full hover:bg-white/10 transition-all flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap"
          >
            <i className="ri-megaphone-line text-lg sm:text-xl"></i>
            List Your Event
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-white/70 px-4">
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {creators.length > 0 ? (
                creators.map((creator, index) => (
                  <div
                    key={creator.id}
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 border-white/30 overflow-hidden bg-gradient-to-br ${gradients[index % gradients.length]} shadow-lg hover:scale-110 hover:z-10 transition-transform cursor-pointer`}
                    title={`${creator.full_name || 'Creator'} — ${creator.event_count} event${creator.event_count !== 1 ? 's' : ''}`}
                  >
                    {creator.avatar_url ? (
                      <img
                        src={creator.avatar_url}
                        alt={creator.full_name || 'Top Creator'}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-xs font-bold">
                        {getInitial(creator.full_name)}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <div
                    key={i}
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br ${gradients[i - 1]} border-2 border-white/30 animate-pulse`}
                  />
                ))
              )}
            </div>
            <span className="text-sm sm:text-base font-medium ml-2">
              {creatorCount !== null ? `${creatorCount.toLocaleString()}+` : '...'} Creators
            </span>
          </div>
          <div className="flex items-center gap-3 opacity-40">
            <i className="ri-instagram-line text-xl sm:text-2xl"></i>
            <i className="ri-twitter-x-line text-xl sm:text-2xl"></i>
            <i className="ri-tiktok-line text-xl sm:text-2xl"></i>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </section>
  );
}
